<script setup lang="ts">
import type { UIColor } from "../../..";
import Overlay from "../../internal/Overlay/Overlay.vue";
import { getTailwindClassForCssVar } from "../../helpers";
import PushButtonSpinner from "./PushButtonSpinner.vue";
import { computed, provide, ref } from "vue";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  color: UIColor;
  colorBorder?: UIColor;
  size: "collapsed" | "small" | "medium" | "large";
  loading?: boolean;
  disabled?: boolean;
  underline?: boolean;
  omitPadding?: boolean;
  shadow?: "small" | "medium" | "large";
}>();

const colorClasses = computed(() => {
  const classes = [];
  if (props.color) {
    const bgClass = getTailwindClassForCssVar(props.color, "bg");
    if (bgClass) {
      classes.push(bgClass);
    }
  }
  if (props.colorBorder) {
    const borderClass = getTailwindClassForCssVar(props.colorBorder, "border");
    if (borderClass) classes.push(borderClass);
  }

  return classes;
});

const buttonSizeClasses = computed(() => {
  if (props.omitPadding) return "p-0";

  switch (props.size) {
    case "small":
      return "px-2 py-1 gap-x-0.5";
    case "medium":
      return "px-2.5 py-1.5 gap-x-1";
    case "large":
      return "px-3.5 py-2 gap-x-1.5";
    default:
      return "p-0";
  }
});

const spinnerSizeClass = computed(() => {
  switch (props.size) {
    case "small":
      return "px-2";
    case "medium":
      return "px-3";
    case "large":
      return "px-4";
    default:
      return "p-1";
  }
});

provide("backgroundColor", props.color);
provide("copyColor", ref<UIColor>());
provide("iconColor", ref<UIColor>());
</script>

<template>
  <div
    :class="[
      'inline-flex flex-nowrap relative select-none w-fit group transition-all items-center',
      colorBorder ? 'border' : '',
      disabled ? 'cursor-not-allowed' : '',
      shadow === 'small'
        ? 'shadow-sm'
        : shadow === 'medium'
        ? 'shadow-md'
        : shadow === 'large'
        ? 'shadow-lg'
        : 'shadow-none',
      ...colorClasses,
    ]"
  >
    <Overlay
      v-if="disabled"
      colorBackground="--color-primary"
      colorForeground="--color-primary"
      opacityBackground="0.075"
      opacityForeground="0.1"
    />

    <div
      :class="[
        props.color === '--color-transparent' ? 'bg-accent-100' : '',
        loading ? 'opacity-100' : 'opacity-0',
        'pointer-events-none z-10 absolute w-full h-full flex items-center justify-center p-1 transition-opacity',
      ]"
    >
      <PushButtonSpinner :class="[spinnerSizeClass]" />
    </div>

    <Button
      v-bind="$attrs"
      :class="[
        'rounded-none inline-flex h-auto items-center select-none border-0 font-inherit',
        'shadow-none cursor-pointer',
        'bg-transparent hover:bg-transparent', // disables built-in hover bg
        buttonSizeClasses,
        loading ? 'opacity-0' : '',
      ]"
    >
      <slot name="iconBefore" />

      <slot name="icon" />

      <span
        v-if="$slots.copy"
        :class="[underline ? 'underline group-hover:no-underline' : '']"
      >
        <slot name="copy" />
      </span>

      <slot name="iconAfter" />
    </Button>
  </div>
</template>
